// src/features/people/PeopleList.jsx - CON FUNCIONALIDAD SHOWDELETED Y RESTAURAR
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Container, Button, Card, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaUserPlus, FaIdCard, FaUserTag, FaEye, FaEyeSlash } from 'react-icons/fa';
import TableWrapper from '../../components/TableWrapper';
import FilterSelect from '../../components/FilterSelect';
import PeopleService from './PeopleService';
import CustomSnackbar from '../../components/CustomSnackbar';
import ExportCsvButton from '../../components/ExportCsvButton';
import { useColumnFilter } from '../../hooks/useColumnFilter';
import { TIPOS_DOCUMENTO_IDENTIDAD } from '../../utils/constants';

// ✅ CLAVE PARA PERSISTENCIA DEL ESTADO showDeleted SOLAMENTE
const SHOW_DELETED_KEY = 'people-list-showDeleted';

function PeopleList() {
  const [people, setPeople] = useState([]);
  const [filteredPeople, setFilteredPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, type: '', message: '' });
  const [gridApi, setGridApi] = useState(null);
  const navigate = useNavigate();

  // ✅ ESTADO CON PERSISTENCIA PARA MOSTRAR DADAS DE BAJA
  const [showDeleted, setShowDeleted] = useState(() => {
    try {
      const saved = sessionStorage.getItem(SHOW_DELETED_KEY);
      return saved ? JSON.parse(saved) : false;
    } catch (error) {
      console.error('Error al recuperar estado showDeleted:', error);
      return false;
    }
  });

  // ✅ FUNCIÓN PARA NORMALIZAR TEXTO REMOVIENDO ACENTOS/TILDES
  const normalizeText = useCallback((text) => {
    if (!text || typeof text !== 'string') return '';
    return text
      .normalize('NFD') // Descompone caracteres con acentos
      .replace(/[\u0300-\u036f]/g, '') // Remueve marcas diacríticas (acentos)
      .toLowerCase()
      .trim();
  }, []);

  // Estados y funciones para filtros dropdown
  const { 
    filterOptions: documentTypeOptions, 
    activeFilters: activeDocumentTypes,
    handleOptionsUpdate: handleDocumentTypeOptionsUpdate,
    handleFiltersChange: handleDocumentTypeChange,
    filterData: filterByDocumentType 
  } = useColumnFilter(people, 'documentType', null, 'people');

  // Estados y funciones para filtros dropdown de régimen
  const { 
    filterOptions: regimeOptions, 
    activeFilters: activeRegimes,
    handleOptionsUpdate: handleRegimeOptionsUpdate,
    handleFiltersChange: handleRegimeChange,
    filterData: filterByRegime 
  } = useColumnFilter(people, 'regime', null, 'people');
  
  // Filtros predefinidos para tipos de documento
  const [predefinedDocTypes] = useState(
    TIPOS_DOCUMENTO_IDENTIDAD.map(tipo => ({
      id: tipo.id,
      label: tipo.descripcion,
      value: tipo.id,
      checked: false
    }))
  );

  // ✅ FUNCIÓN PARA CARGAR PERSONAS CON PERSISTENCIA DE ESTADO
  const loadPeople = useCallback(async () => {
    try {
      setLoading(true);
      const data = await PeopleService.getAllPeople({ 
        showDeleted: showDeleted 
      });
      setPeople(data);
      setFilteredPeople(data);
    } catch (error) {
      console.error('Error al cargar personas:', error);
      setSnackbar({
        open: true,
        type: 'error',
        message: 'Error al cargar la lista de personas'
      });
    } finally {
      setLoading(false);
    }
  }, [showDeleted]);

  // Aplicar filtros cuando cambian los filtros activos o los datos
  useEffect(() => {
    let result = [...people];
    
    // Aplicar filtros en cadena
    if (activeDocumentTypes.length > 0) {
      result = filterByDocumentType(result);
    }
    
    if (activeRegimes.length > 0) {
      result = filterByRegime(result);
    }
    
    setFilteredPeople(result);
  }, [people, activeDocumentTypes, activeRegimes, filterByDocumentType, filterByRegime]);

  // Hook useEffect para cargar datos al montar el componente
  useEffect(() => {
    loadPeople();
  }, [loadPeople]);

  // ✅ PERSISTIR ESTADO showDeleted
  useEffect(() => {
    try {
      sessionStorage.setItem(SHOW_DELETED_KEY, JSON.stringify(showDeleted));
    } catch (error) {
      console.error('Error al guardar estado showDeleted:', error);
    }
  }, [showDeleted]);

  // Funciones de manejo para acciones de tabla
  const handleViewPerson = useCallback((person) => {
    if (actionLoading) return;
    navigate(`/people/${person.id}`);
  }, [navigate, actionLoading]);

  const handleEditPerson = useCallback((person) => {
    if (actionLoading) return;
    navigate(`/people/${person.id}/edit`);
  }, [navigate, actionLoading]);

  // ✅ MODIFICADO: handleDeletePerson ahora puede DAR DE BAJA o RESTAURAR
  const handleDeletePerson = useCallback(async (person) => {
    if (actionLoading) {
      console.warn('Ya se está procesando una acción, ignorando clic');
      return;
    }

    // ✅ MOSTRAR nombre correcto según tipo de persona
    const displayName = person.type === 'F' 
      ? `${person.firstName} ${person.surname || ''}`.trim()
      : person.businessName || 'Persona jurídica';

    // ✅ DETERMINAR SI ES DAR DE BAJA O RESTAURAR
    const isDeactivated = person.deletedDate !== null;
    
    if (isDeactivated) {
      // ✅ RESTAURAR PERSONA
      if (window.confirm(`¿Está seguro que desea restaurar a ${displayName}?`)) {
        try {
          setActionLoading(true);
          const result = await PeopleService.restorePerson(person.id);
          
          if (result.statusCode >= 200 && result.statusCode < 300) {
            setSnackbar({
              open: true,
              type: 'success',
              message: 'Persona restaurada correctamente'
            });
            await loadPeople();
          } else {
            const errorMessage = result.data?.message || `Error del servidor (${result.statusCode})`;
            setSnackbar({
              open: true,
              type: 'error',
              message: errorMessage
            });
          }
        } catch (error) {
          console.error('Error al restaurar persona:', error);
          setSnackbar({
            open: true,
            type: 'error',
            message: 'Error inesperado al restaurar la persona'
          });
        } finally {
          setActionLoading(false);
        }
      }
    } else {
      // ✅ DAR DE BAJA PERSONA usando DELETE
      if (window.confirm(`¿Está seguro que desea dar de baja a ${displayName}?`)) {
        try {
          setActionLoading(true);
          await PeopleService.deletePerson(person.id);
          
          setSnackbar({
            open: true,
            type: 'success',
            message: 'Persona dada de baja correctamente'
          });
          
          await loadPeople();
          
        } catch (error) {
          console.error('Error al dar de baja persona:', error);
          setSnackbar({
            open: true,
            type: 'error',
            message: 'Error al dar de baja la persona'
          });
        } finally {
          setActionLoading(false);
        }
      }
    }
  }, [actionLoading, loadPeople]);

  const handleAddPerson = useCallback(() => {
    if (actionLoading) return;
    navigate('/people/add');
  }, [navigate, actionLoading]);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  // ✅ FUNCIÓN PARA TOGGLE MOSTRAR DADAS DE BAJA
  const handleToggleShowDeleted = useCallback(() => {
    setShowDeleted(prev => !prev);
  }, []);

  // ✅ CONTAR PERSONAS ACTIVAS Y DADAS DE BAJA
  const activePeopleCount = people.filter(person => !person.deletedDate).length;
  const deletedPeopleCount = people.filter(person => person.deletedDate).length;

  // ✅ CONFIGURACIÓN MEJORADA PARA FILTROS DE TEXTO SIN ACENTOS
  const textFilterParams = useMemo(() => ({
    debounceMs: 200,
    suppressAndOrCondition: true,
    // ✅ Función personalizada para comparar texto ignorando acentos
    textMatcher: ({ filterOption, value, filterText }) => {
      if (!filterText) return true;
      
      const normalizedValue = normalizeText(value);
      const normalizedFilter = normalizeText(filterText);
      
      switch (filterOption) {
        case 'contains':
          return normalizedValue.includes(normalizedFilter);
        case 'notContains':
          return !normalizedValue.includes(normalizedFilter);
        case 'equals':
          return normalizedValue === normalizedFilter;
        case 'notEqual':
          return normalizedValue !== normalizedFilter;
        case 'startsWith':
          return normalizedValue.startsWith(normalizedFilter);
        case 'endsWith':
          return normalizedValue.endsWith(normalizedFilter);
        default:
          return normalizedValue.includes(normalizedFilter);
      }
    }
  }), [normalizeText]);

  // ✅ CONFIGURACIÓN ESPECÍFICA PARA CAMPOS QUE NO NECESITAN NORMALIZACIÓN (números, etc.)
  const numericFilterParams = useMemo(() => ({
    debounceMs: 200,
    suppressAndOrCondition: true
  }), []);

  // ✅ DEFINICIÓN DE COLUMNAS CORREGIDA PARA PERSONAS FÍSICAS Y JURÍDICAS
  const columnDefs = useMemo(() => [
    { 
      headerName: 'Estado', 
      field: 'deletedDate', 
      width: 80,
      maxWidth: 80,
      minWidth: 80,
      resizable: false,
      cellRenderer: params => {
        const isDeleted = params.value !== null;
        return (
          <Badge 
            bg={isDeleted ? 'secondary' : 'success'} 
            className="w-100"
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.35rem' }}
          >
            {isDeleted ? 'Baja' : 'Activa'}
          </Badge>
        );
      },
      filter: false,
      floatingFilter: false
    },
    { 
      headerName: 'Nombre/Razón Social', 
      // ✅ CORREGIDO: Usar valueGetter para mostrar firstName O businessName según el tipo
      valueGetter: (params) => {
        const data = params.data;
        if (data.type === 'F') {
          return data.firstName || '';
        } else {
          return data.businessName || '';
        }
      },
      flex: 1.5,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: textFilterParams, // ✅ Usa filtro sin acentos
      // ✅ Agregar cellRenderer para mostrar indicador visual del tipo
      cellRenderer: (params) => {
        const data = params.data;
        const name = params.value || '';
        const typeIndicator = data.type === 'F' ? '👤' : '🏢';
        return `${typeIndicator} ${name}`;
      }
    },
    { 
      headerName: 'Apellido/Tipo', 
      // ✅ CORREGIDO: Mostrar apellido para físicas, tipo para jurídicas
      valueGetter: (params) => {
        const data = params.data;
        if (data.type === 'F') {
          return data.surname || '';
        } else {
          return 'Persona Jurídica';
        }
      },
      flex: 1,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: textFilterParams // ✅ Usa filtro sin acentos
    },
    { 
      headerName: 'Documento', 
      field: 'documentType', 
      width: 110,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: textFilterParams, // ✅ Usa filtro sin acentos
    },
    { 
      headerName: 'Numero', 
      field: 'document', 
      width: 120,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: numericFilterParams // ✅ Para números no necesita normalización
    },
    { 
      headerName: 'Teléfono', 
      // ✅ CORREGIDO: Formatear teléfono de manera segura
      valueGetter: (params) => {
        const phone = params.data.phone;
        if (!phone) return '';
        
        // Si es número, convertir a string y formatear
        if (typeof phone === 'number') {
          return phone.toString().padStart(9, '0');
        }
        
        // Si ya es string, usarlo tal como está
        return phone.toString();
      },
      width: 120,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: numericFilterParams // ✅ Para números no necesita normalización
    },
    { 
      headerName: 'Dirección', 
      field: 'address', 
      flex: 1.5,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: textFilterParams, // ✅ Usa filtro sin acentos
      // ✅ Manejar valores nulos/vacíos
      valueGetter: (params) => {
        return params.data.address || '';
      }
    },
    { 
      headerName: 'Régimen', 
      field: 'regime', 
      width: 120,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: textFilterParams // ✅ Usa filtro sin acentos
    }
  ], [textFilterParams, numericFilterParams]);

  return (
    <Container fluid style={{ padding: 0 }}>
      <Card>
        <Card.Header className="bg-primary text-white text-center">
          <h5 className="mb-0">
            Personas
            {/* ✅ INDICADORES DE ESTADO */}
            <Badge bg="light" text="dark" className="ms-2">
              {showDeleted 
                ? `Todas (${people.length})` 
                : `Activas (${activePeopleCount})`
              }
            </Badge>
          </h5>
        </Card.Header>
        
        {/* Botón de agregar y filtros */}
        <div className="d-flex justify-content-between align-items-center p-2 bg-light border-bottom">
          {/* Filtros dropdown */}
          <div className="d-flex gap-2">
            <FilterSelect
              options={predefinedDocTypes.length > 0 ? predefinedDocTypes : documentTypeOptions}
              activeFilters={activeDocumentTypes}
              onFiltersChange={handleDocumentTypeChange}
              onOptionsUpdate={handleDocumentTypeOptionsUpdate}
              title="Filtrar por tipo de documento"
              filterKey="people-document-types"
              icon={<FaIdCard />}
            />
            
            <FilterSelect
              options={regimeOptions}
              activeFilters={activeRegimes}
              onFiltersChange={handleRegimeChange}
              onOptionsUpdate={handleRegimeOptionsUpdate}
              title="Filtrar por régimen"
              filterKey="people-regimes"
              icon={<FaUserTag />}
            />
          </div>
          
          {/* Botones de acción */}
          <div className="d-flex gap-2">
            <ExportCsvButton gridApi={gridApi} moduleName="personas" />
            {/* ✅ BOTÓN PARA MOSTRAR/OCULTAR DADAS DE BAJA */}
            <Button 
              variant={showDeleted ? "warning" : "outline-secondary"}
              size="sm"
              onClick={handleToggleShowDeleted}
              className="d-flex align-items-center"
              disabled={loading}
            >
              {showDeleted ? (
                <>
                  <FaEyeSlash className="me-1" /> Ocultar Dadas de Baja
                </>
              ) : (
                <>
                  <FaEye className="me-1" /> Ver Dadas de Baja
                  {deletedPeopleCount > 0 && (
                    <Badge bg="secondary" className="ms-1">
                      {deletedPeopleCount}
                    </Badge>
                  )}
                </>
              )}
            </Button>
            
            {/* Botón Agregar Persona */}
            <Button 
              variant="primary" 
              onClick={handleAddPerson}
              className="d-flex align-items-center"
              disabled={actionLoading}
              size="sm"
            >
              <FaUserPlus className="me-1" /> Agregar Persona
            </Button>
          </div>
        </div>
        
        <Card.Body>
          {loading ? (
            <div className="text-center my-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
              <p className="mt-2">Cargando datos...</p>
            </div>
          ) : (
            <TableWrapper
              columnDefs={columnDefs}
              rowData={filteredPeople}
              tableType="personas"
              onGridReady={(e) => setGridApi(e.api)}
              onView={handleViewPerson}
              onEdit={handleEditPerson}
              onDelete={handleDeletePerson}
              customGridOptions={{
                domLayout: 'autoHeight',
                floatingFilter: true,
                headerHeight: 40,
                pagination: true,
                paginationPageSize: 20,
                defaultColDef: {
                  flex: 1,
                  minWidth: 100,
                  resizable: true,
                  sortable: true,
                  filter: 'agTextColumnFilter',
                  floatingFilter: true
                }
              }}
            />
          )}
        </Card.Body>
      </Card>

      <CustomSnackbar
        open={snackbar.open}
        type={snackbar.type}
        message={snackbar.message}
        onClose={handleCloseSnackbar}
      />
    </Container>
  );
}

export default PeopleList;